import { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useSearchParams } from 'react-router-dom';
import ExpertFilterForm from './ExpertFilterForm';
import { Button, Table, Space, Spin, Typography, Divider, Dropdown, Modal, Alert, Form, Input, message, Tooltip } from 'antd';
import { fullPagerConfig } from '@/utils/property';
import { changeExpertStatusApi, getExpertListApi } from '@services/Experts';
import ChangeCredibilityModal from '../profile/ChangeCredibilityModal';
import { logExpertChangeStatus } from '@/utils/expertLogger';
import { useDispatch, useSelector } from 'react-redux';
import { expertActions } from './store/expertSlice';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { localStorageGet } from '@core/cookie';
import { authdictionaryApi } from '@services/Conference/sign';
import { buildAuthDictMaps, formatOfficeAndPost } from '@/utils/utils';
import WorkExperiences from '@/components/common/WorkExperiences';
import ExpertInfoCard from '@/components/common/ExpertInfoCard';
import ExpertProfileDrawer from '../profile';
import PageHeader from '@components/PageHeader/PageHeader';
import './index.less';
const { Text } = Typography;

const { setFilter, setCursor } = expertActions;

// 使用 @utils 中的院士常量与工具函数

const StyledExpertProInfo = styled.div`
    .expert-proinfo {
        //max-width: 250px;
        //overflow: hidden;
        //text-overflow: ellipsis;
        //display: -webkit-box;
        //-webkit-line-clamp: 2;
        //-webkit-box-orient: vertical;
    }

    .expert-proinfo__label {
        font-weight: bold;
        color: #888;
    }

    .expert-proinfo__content {
        flex: 1;
    }

    .toggle-expansion {
        color: #1890ff;
        cursor: pointer;
        font-size: 12px;
        margin-top: 4px;

        .anticon {
            transition: transform 0.3s;
        }
    }
`;

const ExpertOverview = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const dispatch = useDispatch();
    const filter = useSelector(state => state.expert.filter);
    const cursor = useSelector(state => state.expert.cursor);
    // console.log(filter, cursor);

    // 渲染table数据的state
    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);

    // 认证字典数据
    const [authDict, setAuthDict] = useState([]);

    // 筛选数据
    const handleFilterFinish = values => {
        const newFilter = {
            ...filter,
            ...values,
            page: 1,
        };
        if (isEmpty(newFilter?.expertLevels)) {
            delete newFilter.expertLevels;
        }
        console.log('filter', newFilter);
        dispatch(setFilter(newFilter));
    };

    const onChangePage = (page, pageSize) => {
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };

    const [loading, setLoading] = useState(false);
    const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
    /** 当前抽屉专家 id：null 未打开；0 为新增；大于 0 为查看/编辑 */
    const [currentExpertId, setCurrentExpertId] = useState(null);
    /** 与「查看 → 点编辑」一致：为 true 时打开抽屉直接进入编辑表单（列表「编辑」） */
    const [profileOpenInEditMode, setProfileOpenInEditMode] = useState(false);

    /** 禁用确认弹窗：当前行数据（正常态专家） */
    const [disableModalExpert, setDisableModalExpert] = useState(null);
    const [disableSubmitting, setDisableSubmitting] = useState(false);
    const [disableForm] = Form.useForm();

    /** 调整可信度弹窗：列表行或详情抽屉中的专家快照 */
    const [credibilityModalExpert, setCredibilityModalExpert] = useState(null);
    /** 详情抽屉在可信度变更成功后刷新详情 */
    const [profileDetailRefreshNonce, setProfileDetailRefreshNonce] = useState(0);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    // 请求认证字典数据
    const requestAuthDict = useCallback(async () => {
        const res = await authdictionaryApi();
        if (!isEmpty(res?.options)) {
            const authDictData = res.options;
            authDictData
                && Array.isArray(authDictData)
                && authDictData.forEach(authType => {
                    authType.labels
                        && Array.isArray(authType.labels)
                        && authType.labels.forEach(label => {
                            label.options
                                && Array.isArray(label.options)
                                && label.options.forEach(l => {
                                    if (isEmpty(l.options)) {
                                        delete l.options;
                                    }
                                });
                        });
                });
            setAuthDict(authDictData);
            console.log('认证字典数据加载完成', authDictData);
        }
    }, []);

    // 获取用户列表
    const getExpertList = useCallback(async () => {
        setLoading(true);
        const res = await getExpertListApi(filter);
        if (res && res.status_code === 200) {
            setTableData(res.data.list);
            setTotal(res.data.totalCount);
        }
        setLoading(false);
    }, [filter]);

    // 初始化认证字典数据
    useEffect(() => {
        requestAuthDict();
    }, [requestAuthDict]);

    // 渲染table数据
    useEffect(() => {
        getExpertList();
    }, [getExpertList]);

    // 是否拥有查看专家综评结果的权限
    const canViewEvalResult = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList?.length) {
            const hasEvaluation = roleList.some(item => item.name === '查看专家综评结果');
            return hasEvaluation;
        }
        return false;
    }, []);

    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        size: 'small',
        total,
        showTotal: () => `共${total}条`,
        current: filter.page,
        pageSize: filter.pageSize,
        onChange: onChangePage,
    };

    const [expandedItems, setExpandedItems] = useState({});

    const toggleExpertProInfo = id => {
        setExpandedItems(prev => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    // 根据认证字典构建映射
    const dictMaps = useMemo(() => buildAuthDictMaps(authDict), [authDict]);

    const handleViewExpert = useCallback(
        row => {
            dispatch(setCursor(row.id));
            setCurrentExpertId(row.id);
            setProfileOpenInEditMode(false);
            setProfileDrawerOpen(true);
        },
        [dispatch],
    );

    const handleEditExpert = useCallback(
        row => {
            dispatch(setCursor(row.id));
            setCurrentExpertId(row.id);
            setProfileOpenInEditMode(true);
            setProfileDrawerOpen(true);
        },
        [dispatch],
    );

    // table 数据相关
    const columns = [
        {
            title: '基本信息',
            dataIndex: 'memUsername',
            key: 'memUsername',
            width: 200,
            render: (_, record) => <ExpertInfoCard expert={record} />,
        },
        {
            title: '单位任职',
            dataIndex: 'ssoMemberWorkExperiencesEntities',
            key: 'ssoMemberWorkExperiencesEntities',
            width: 200,
            render: (_state, row) => (
                <WorkExperiences
                    workExperiences={row.ssoMemberWorkExperiencesEntities}
                    customFormatter={(item, context) => {
                        const officePostDisplay = formatOfficeAndPost(item, context);
                        const unitName = item.unitName || '';
                        return {
                            unitName,
                            officePostDisplay,
                        };
                    }}
                    formatterContext={dictMaps}
                />
            ),
        },
        {
            title: '专业信息',
            key: 'proInfo',
            width: 300,
            render: (_, record) => {
                const proInfoFields = {
                    academicPosition: '学术任职',
                    researchAchievements: '研究成果',
                    managementExpertises: '管理专长',
                    medicalExpertises: '医学专长',
                    honoraryTitles: '荣誉头衔',
                };

                const proInfoItems = Object.entries(proInfoFields)
                    .map(([key, label]) => {
                        // 荣誉头衔处理
                        if (key === 'honoraryTitles') {
                            let honoraryTitles = [];
                            if (!!record[key]) {
                                try {
                                    // 支持字符串、数组、异常情况
                                    if (Array.isArray(record[key])) {
                                        honoraryTitles = record[key];
                                    } else if (typeof record[key] === 'string') {
                                        // 先尝试 JSON 解析
                                        try {
                                            const parsed = JSON.parse(record[key]);
                                            if (Array.isArray(parsed)) {
                                                honoraryTitles = parsed;
                                            } else if (typeof parsed === 'string') {
                                                honoraryTitles = [parsed];
                                            } else if (parsed && typeof parsed === 'object') {
                                                honoraryTitles = Object.values(parsed);
                                            }
                                        } catch (_jsonErr) {
                                            // 不是 JSON，直接按逗号/顿号/空格分割
                                            honoraryTitles = record[key].split(/[、,，\s]+/).filter(Boolean);
                                        }
                                    }
                                } catch (error) {
                                    console.error('荣誉头衔解析失败:', error);
                                }
                            }
                            return { label, value: honoraryTitles.filter(Boolean).join('、') };
                        } else {
                            // 其他字段处理
                            return { label, value: record[key] };
                        }
                    })
                    .filter(item => item.value);

                if (!proInfoItems.length) {
                    return <Text type="secondary">暂无</Text>;
                }

                return (
                    <StyledExpertProInfo>
                        {proInfoItems.map((item, index) => (
                            <div
                                key={`${record.id}_${index}`}
                                className="expert-proinfo"
                                style={{
                                    display: !expandedItems[record.id] && index > 0 ? 'none' : 'block',
                                }}
                            >
                                <span className="expert-proinfo__label">{item.label}：</span>
                                <span className="expert-proinfo__content">{item.value}</span>
                            </div>
                        ))}
                        {proInfoItems.length > 1 && (
                            <div className="toggle-expansion" onClick={() => toggleExpertProInfo(record.id)}>
                                <span className="anticon" style={{ transform: expandedItems[record.id] ? 'rotate(180deg)' : 'none' }}>
                                    <i className="bi bi-caret-down-fill" style={{ fontSize: 12 }}></i>
                                </span>
                                <span>{expandedItems[record.id] ? '收起' : `展开${proInfoItems.length}项`}</span>
                            </div>
                        )}
                    </StyledExpertProInfo>
                );
            },
        },
        {
            title: '专家价值',
            dataIndex: 'totalScore',
            width: 200,
            hidden: !canViewEvalResult,
            render: (_, record) =>
                !!record.totalScore ? (
                    <>
                        <div>合作友好度： {record.friendlinessScore}分</div>
                        <div>报告专业度： {record.professionScore}分</div>
                        <div>价值度： {record.totalScore}分</div>
                    </>
                ) : (
                    '-'
                ),
        },
        // {
        //     title: '平台用户',
        //     dataIndex: 'authentication',
        //     key: 'authentication',
        //     width: 60,
        //     render: (state, row) => <>{row.memCard ? <span style={{ color: '#52c41a' }}>是</span> : '否'}</>
        // },
        {
            title: '平台认证信息',
            dataIndex: 'authentication',
            key: 'authentication',
            width: 100,
            render: (_state, row) => <span>{getAuthType(row.authentication)}</span>,
        },
        {
            title: '最后更新',
            key: 'lastUpdate',
            width: 140,
            render: (_, record) => {
                const createdParts = [record.createdBy, record.createdAt].filter(v => v != null && v !== '');
                const creationTooltipTitle = createdParts.length ? `${createdParts.join(' ')} 新增` : '';

                const inner = (
                    <div style={{ cursor: 'default' }}>
                        {record.updatedBy != null && record.updatedBy !== '' ? <div>{record.updatedBy}</div> : null}
                        {record.updatedAt != null && record.updatedAt !== '' ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {record.updatedAt}
                            </Text>
                        ) : null}
                    </div>
                );

                return creationTooltipTitle ? <Tooltip title={creationTooltipTitle}>{inner}</Tooltip> : inner;
            },
        },
        {
            title: '操作',
            dataIndex: 'state',
            key: 'state',
            fixed: 'right',
            width: 140,
            render: (_state, row) => {
                const items = [
                    {
                        label: '调整可信度',
                        key: 'changeCredibility',
                    },
                    {
                        label: row?.status === 0 ? '禁用' : '启用',
                        key: 'changeStatus',
                        danger: row?.status === 0,
                    },
                ];

                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button size="small" type="link" onClick={() => handleViewExpert(row)}>
                            查看
                        </Button>
                        <Button size="small" type="link" onClick={() => handleEditExpert(row)}>
                            编辑
                        </Button>
                        <Dropdown
                            menu={{
                                items,
                                onClick: info => handleOperationMenuClick(info, row),
                            }}
                            placement="bottomRight"
                        >
                            <Button type="link" size="small" className="more-operation">
                                <i className="bi bi-three-dots"></i>
                            </Button>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    // 认证类型
    const getAuthType = type => {
        switch (type) {
            case 'A01':
                return '医务人员';
            case 'A02':
                return '政府官员';
            case 'A03':
                return '商业人士';
            case 'A04':
                return '教育科研人士';
            case 'A06':
                return '社会团体人士';
            default:
                return '无';
        }
    };

    // 新增专家：与查看详情共用 ExpertProfileDrawer（expertId=0 表示新建表单）
    const handleClickAddExpert = useCallback(() => {
        setCurrentExpertId(0);
        setProfileOpenInEditMode(false);
        setProfileDrawerOpen(true);
    }, []);

    /** 表单内「查看同名专家详情」等：切换到对应专家抽屉 */
    const handleOpenExpertDetailFromForm = useCallback(
        id => {
            if (!id) {
                return;
            }
            dispatch(setCursor(id));
            setCurrentExpertId(id);
            setProfileOpenInEditMode(false);
            setProfileDrawerOpen(true);
        },
        [dispatch],
    );

    // 兼容旧链接 /dc/experts?detail=、?create=1
    useEffect(() => {
        const detail = searchParams.get('detail');
        const create = searchParams.get('create');
        if (detail == null && create !== '1') {
            return;
        }
        const next = new URLSearchParams(searchParams);
        let changed = false;
        if (detail != null && detail !== '') {
            const id = parseInt(detail, 10);
            if (Number.isFinite(id) && id > 0) {
                dispatch(setCursor(id));
                setCurrentExpertId(id);
                setProfileOpenInEditMode(false);
                setProfileDrawerOpen(true);
            }
            next.delete('detail');
            changed = true;
        }
        if (create === '1') {
            setCurrentExpertId(0);
            setProfileOpenInEditMode(false);
            setProfileDrawerOpen(true);
            next.delete('create');
            changed = true;
        }
        if (changed) {
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams, dispatch]);

    useEffect(() => {
        if (disableModalExpert) {
            disableForm.resetFields();
        }
    }, [disableModalExpert, disableForm]);

    const confirmEnableExpert = useCallback(
        row => {
            Modal.confirm({
                title: '启用专家',
                content: '确认启用该专家？启用后可正常参与项目。',
                okText: '确定',
                cancelText: '取消',
                onOk: async () => {
                    const res = await changeExpertStatusApi({ id: row.id, status: 0 });
                    if (res?.status_code === 200) {
                        message.success('已启用该专家');
                        try {
                            await logExpertChangeStatus(row.id, { fromStatus: 1, toStatus: 0 }, true);
                        } catch (logErr) {
                            console.error('启用专家操作日志上报失败', logErr);
                        }
                        getExpertList();
                    } else {
                        try {
                            await logExpertChangeStatus(row.id, { fromStatus: 1, toStatus: 0 }, false, res?.message || '更改状态失败');
                        } catch (_e) {
                            /* 忽略日志失败 */
                        }
                    }
                },
            });
        },
        [getExpertList],
    );

    const handleDisableModalOk = useCallback(async () => {
        try {
            const values = await disableForm.validateFields();
            setDisableSubmitting(true);
            const res = await changeExpertStatusApi({ id: disableModalExpert.id, status: 1 });
            if (res?.status_code === 200) {
                message.success('已禁用该专家');
                try {
                    await logExpertChangeStatus(disableModalExpert.id, { fromStatus: 0, toStatus: 1, disableReason: values.reason }, true);
                } catch (logErr) {
                    console.error('禁用专家操作日志上报失败', logErr);
                }
                setDisableModalExpert(null);
                disableForm.resetFields();
                getExpertList();
            } else {
                try {
                    await logExpertChangeStatus(disableModalExpert.id, { fromStatus: 0, toStatus: 1, disableReason: values.reason }, false, res?.message || '更改状态失败');
                } catch (_e) {
                    /* 忽略日志失败 */
                }
            }
        } catch (e) {
            if (e?.errorFields) {
                return Promise.reject();
            }
        } finally {
            setDisableSubmitting(false);
        }
    }, [disableForm, disableModalExpert, getExpertList]);

    const handleOperationMenuClick = useCallback(
        (info, row) => {
            const { key, domEvent } = info;
            domEvent?.stopPropagation?.();
            if (key === 'changeCredibility') {
                setCredibilityModalExpert(row);
                return;
            }
            if (key === 'changeStatus') {
                if (row.status === 1) {
                    confirmEnableExpert(row);
                } else {
                    setDisableModalExpert(row);
                }
            }
        },
        [confirmEnableExpert],
    );

    const handleCredibilityModalSuccess = useCallback(() => {
        const changedId = credibilityModalExpert?.id;
        setCredibilityModalExpert(null);
        getExpertList();
        if (changedId && changedId === currentExpertId && currentExpertId > 0) {
            setProfileDetailRefreshNonce(n => n + 1);
        }
    }, [credibilityModalExpert, currentExpertId, getExpertList]);

    const handleOpenChangeCredibilityFromDrawer = useCallback(expertSnapshot => {
        if (!expertSnapshot?.id) {
            return;
        }
        setCredibilityModalExpert(expertSnapshot);
    }, []);

    useEffect(() => {
        document.title = '专家管理-专家库';
    }, []);

    return (
        <div className="page-container list-page-container expert-list-page">
            <PageHeader title="专家管理" breadcrumb={[{ label: '专家库', path: '/dc/experts' }, { label: '专家管理' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <ExpertFilterForm handleFilterFinish={handleFilterFinish} filter={filter} setFilter={setFilter} />
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container dc-expert-table-region">
                <div className="main-toolbar dc-expert-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={handleClickAddExpert}>
                                新增专家
                            </Button>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={getExpertList} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>
                <div className="list-table-wrap dc-expert-table-wrap">
                    <Spin spinning={loading} delay={500}>
                        <Table
                            className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                            columns={columns.filter(item => !item.hidden)}
                            dataSource={tableData}
                            rowKey={record => record.id}
                            rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                            bordered
                            sticky
                            size="medium"
                            pagination={paginationProps}
                            scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                        />
                    </Spin>
                </div>
            </div>

            <ExpertProfileDrawer
                open={profileDrawerOpen}
                expertId={currentExpertId}
                openInEditMode={profileOpenInEditMode}
                onClose={() => {
                    setProfileDrawerOpen(false);
                    setCurrentExpertId(null);
                    setProfileOpenInEditMode(false);
                }}
                onRefreshList={getExpertList}
                canViewEvaluation={canViewEvalResult}
                onOpenChangeCredibility={handleOpenChangeCredibilityFromDrawer}
                profileDetailRefreshNonce={profileDetailRefreshNonce}
                onOpenExpertDetail={handleOpenExpertDetailFromForm}
            />

            <ChangeCredibilityModal open={!!credibilityModalExpert} expert={credibilityModalExpert} onCancel={() => setCredibilityModalExpert(null)} onSuccess={handleCredibilityModalSuccess} />

            <Modal
                title="禁用专家"
                open={!!disableModalExpert}
                onCancel={() => {
                    setDisableModalExpert(null);
                    disableForm.resetFields();
                }}
                onOk={handleDisableModalOk}
                okText="确定"
                cancelText="取消"
                okButtonProps={{ danger: true }}
                confirmLoading={disableSubmitting}
                destroyOnHidden
                width={560}
            >
                <Alert type="error" title="禁用后，该专家将不能参与项目，已参与的项目数据不受影响。禁用专家会有“禁用”标记。" style={{ marginBottom: 10 }} />

                {disableModalExpert ? (
                    <div style={{ marginBottom: 16 }}>
                        <ExpertInfoCard expert={disableModalExpert} showDetailButton={false} />
                    </div>
                ) : null}

                <Form form={disableForm} layout="vertical" preserve={false}>
                    <Form.Item name="reason" label="禁用理由" rules={[{ required: true, message: '请填写禁用理由' }]}>
                        <Input.TextArea rows={4} placeholder="填写禁用理由" maxLength={500} showCount />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ExpertOverview;
